import { pick } from 'lodash';
import { type TObjectTypesMap, getSubTypeIds } from 'utils/associations';
import type { TMemberType, TMemberTypesItem } from '@timeedit/types';
import {
  EActivityDraftGroupConflictModel,
  type TActivityDraftGroupConfig,
  ZActivityDraftGroupConfigSchemaSettingsForTheObjectToBeCreated,
} from '@timeedit/types/lib/types/activityGroup.type';

export type TParentGroup = NonNullable<
  TActivityDraftGroupConfig['parentGroup']
>;
export type TObjectSettings = NonNullable<TParentGroup['objectConfig']>;
export type TOrganizationNodes = NonNullable<
  TActivityDraftGroupConfig['organizationNodes']
>;

export const DEFAULT_PARENT_GROUP: TParentGroup = {
  minAmountOfTracksForSeriesToBeElligableAsParent: 1,
  maxAmountOfTracksForSeriesToBeElligableAsParent: 1,
};

export const DEFAULT_ORGANIZATION_NODES: TOrganizationNodes = {
  staticList: [],
  inheritFromPrimaryObject: false,
};

const conflictModelsWithParentGroups = Object.values(
  EActivityDraftGroupConflictModel
).filter((conflictModel) => conflictModel.endsWith('_WITH_PARENT_GROUPS'));

export const conflictModelHasParentGroups = (
  conflictModel: EActivityDraftGroupConflictModel | undefined
) => !!conflictModel && conflictModelsWithParentGroups.includes(conflictModel);

/** The part of a config that describes the object to be created - the same shape as the parent group's object config */
export const pickObjectSettings = (
  config: TActivityDraftGroupConfig
): TObjectSettings =>
  ({
    ...pick(
      config,
      ZActivityDraftGroupConfigSchemaSettingsForTheObjectToBeCreated.keyof()
        .options
    ),
    // Always set, so that the parent group's object config never falls back to the child groups' organization nodes
    organizationNodes: config.organizationNodes ?? DEFAULT_ORGANIZATION_NODES,
  }) as TObjectSettings;

/** The parent group a config should have after its conflict model changed */
export const parentGroupForConflictModel = (
  parentGroup: TParentGroup | undefined,
  conflictModel: EActivityDraftGroupConflictModel
): TParentGroup | undefined => {
  if (!conflictModelHasParentGroups(conflictModel)) return undefined;
  return parentGroup ?? DEFAULT_PARENT_GROUP;
};

export const updateParentObjectConfig = (
  parentGroup: TParentGroup,
  key: string,
  value: unknown
): TParentGroup => ({
  ...parentGroup,
  objectConfig: {
    ...parentGroup.objectConfig,
    [key]: value,
  } as TObjectSettings,
});

/** The object type of the parent groups - the child groups' one unless configured separately */
export const parentGroupObjectType = (config: TActivityDraftGroupConfig) =>
  config.parentGroup?.objectConfig?.objectType ?? config.objectType;

/** Whether a membership config allows objects of the two types to be members of each other, i.e. has them on different levels. Types can be the same */
export const membershipBetweenObjectTypesExists = (
  childType: number,
  parentType: number,
  memberships: TMemberType[],
  objectTypesMap: TObjectTypesMap
) => {
  // A membership on a type also applies to its sub types
  const covers = (item: TMemberTypesItem, typeId: number) =>
    getSubTypeIds(Number(item.type), objectTypesMap).includes(typeId);

  return memberships.some((membership) => {
    // Widen `TMemberTypesItem[] | []`, whose union methods can type their callback params as `never`
    const memberTypes: TMemberTypesItem[] = membership.memberTypes;
    return memberTypes.some(
      (childItem) =>
        covers(childItem, childType) &&
        memberTypes.some(
          (parentItem) =>
            parentItem.memberLevel !== childItem.memberLevel &&
            covers(parentItem, parentType)
        )
    );
  });
};

/** Parent groups can only be created if child groups can be made members of them */
export const missingParentChildMembership = (
  config: TActivityDraftGroupConfig,
  memberships: TMemberType[],
  objectTypesMap: TObjectTypesMap
) => {
  if (!conflictModelHasParentGroups(config.conflictModel)) return false;
  const childType = config.objectType;
  const parentType = parentGroupObjectType(config);
  // Missing object types are reported by the schema validation
  if (!childType || !parentType) return false;
  return !membershipBetweenObjectTypesExists(
    childType,
    parentType,
    memberships,
    objectTypesMap
  );
};
